import {
  Button,
  Layout,
  Modal,
  Radio,
  Table,
  Typography,
} from 'antd';
import React, {
  FC, useRef, useState,
} from 'react';
import { RequestError } from '@ynomia/client';
import dayjs from 'dayjs';
import { EditOutlined } from '@ant-design/icons';
import {
  applyPlannedTwinIDs,
  planPlannedTwinIDs,
  resetPlannedTwinIDUpdate,
  setPlannedTwinIDUpdateMode,
} from '../../actions';
import {
  getPlannedTwinIdsReportFieldsKeyedByPlanID,
  getPlannedTwinIDUpdateToolSegmentedPicklistColumns,
  getPlannedTwinIDUpdateToolSegmentedPicklistDefaultValue,
  getProjectName,
  getScratchProjectCode,
  getPlannedTwinIDUpdateToolFlags,
  getTenant,
  getPlannedTwinIDUpdateToolExcelData,
  getPlanConfigurationsKeyedById,
  getProjectId,
} from '../../selectors';
import { fetchBlueprint, update } from '../../services';
import { PageProps } from '../../config/types';
import PlannedTwinIDUpdateToolResults from './PlannedTwinIDUpdateToolPlanResults';
import { PlannedTwinIDUpdateMode } from '../../config/constants';
import {
  applyUploadCompleteModal,
  displayDateInProjectTime,
  exportToSpreadsheet,
  planUploadCompleteModal,
  planUploadFailedModal,
} from '../../utils';
import { getContextStores } from '../../context';
import { modal } from '../../antdProvider';
import styles from './styles.module.less';
import {
  SegmentedPicklist,
  UpdateToolUploadPanel,
  UpdateToolLogStats,
} from '../../components/atoms';
import ModalForm from '../../components/atoms/ModalForm';

const { Content } = Layout;

const PlannedTwinIDUpdateTool: FC<PageProps> = () => {
  const contextStores = getContextStores();
  const {
    plannedTwinIDUpdateState, plannedTwinIDUpdateDispatch,
  } = contextStores;
  const inputRef = useRef<HTMLInputElement>(null);

  /* Selectors */
  const projectCode = getScratchProjectCode(contextStores);
  const tenant = getTenant(contextStores);
  const plannedTwinIDUpdateToolFlags = getPlannedTwinIDUpdateToolFlags(contextStores);
  const project = getProjectName(contextStores);
  const jsonExportData = getPlannedTwinIDUpdateToolExcelData(contextStores);
  const reportFieldsKeyedByPlanID = getPlannedTwinIdsReportFieldsKeyedByPlanID(contextStores);
  const planPicklistColumns = getPlannedTwinIDUpdateToolSegmentedPicklistColumns(contextStores);
  const planConfigurationsKeyedById = getPlanConfigurationsKeyedById(contextStores);
  const planPicklistDefaultValue = getPlannedTwinIDUpdateToolSegmentedPicklistDefaultValue(
    contextStores,
  );
  const projectId = getProjectId(contextStores);

  const [
    selectedPlan,
    setSelectedPlan,
  ] = useState<{ planID: string, assetTypeId: string } | undefined>(
    planPicklistDefaultValue,
  );
  const [
    isEditPlanLabelModalOpen,
    setIsEditPlanLabelModalOpen,
  ] = useState(false);
  const [
    isEditPlanLabelSubmitLoading,
    setIsEditPlanLabelSubmitLoading,
  ] = useState(false);

  const {
    plannedUpdatedPlannedTwinID,
    isApplyingPlannedTwinIDs,
    isPlanningPlannedTwinIDs,
    fileName,
    fileContents,
    plannedTwinIDUpdateMode,
    error,
  } = plannedTwinIDUpdateState || {};

  const {
    errorCount = 0,
    warningCount = 0,
    infoCount = 0,
  } = plannedUpdatedPlannedTwinID?.logging || {};

  const applyEnabled = (): boolean => {
    if (isPlanningPlannedTwinIDs) return false;
    const applyingWithError = plannedTwinIDUpdateToolFlags?.applying_with_error;
    const apply = plannedTwinIDUpdateToolFlags?.apply;

    if (errorCount > 0 && !applyingWithError) return false;
    if (error && !applyingWithError) return false;
    if (!apply) return false;

    return true;
  };

  const handleChangeCsvOption = (mode: PlannedTwinIDUpdateMode) => {
    setPlannedTwinIDUpdateMode(mode, plannedTwinIDUpdateDispatch);
  };

  const handlePlannedTwinIDCsv = async (
    name: string,
    result: string,
    mode: PlannedTwinIDUpdateMode,
  ) => {
    resetPlannedTwinIDUpdate(plannedTwinIDUpdateDispatch);

    try {
      await planPlannedTwinIDs(
        name,
        result,
        tenant,
        projectCode,
        selectedPlan?.assetTypeId!,
        selectedPlan?.planID!,
        mode,
        plannedTwinIDUpdateDispatch,
      );
      planUploadCompleteModal();
    } catch (err) {
      planUploadFailedModal(err as RequestError);
    }
  };

  const onFileUpload = (file) => {
    if (!selectedPlan) {
      modal.error({
        title: 'Error',
        content: 'Please select a valid Plan',
      });
      return;
    }
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      handlePlannedTwinIDCsv(file.name, reader.result as string, plannedTwinIDUpdateMode);
    };
  };

  const handleApplyPlannedTwinIDs = async () => {
    try {
      await applyPlannedTwinIDs(
        fileContents as string,
        tenant,
        projectCode,
        selectedPlan?.assetTypeId!,
        selectedPlan?.planID!,
        plannedTwinIDUpdateMode,
        plannedTwinIDUpdateDispatch,
      );
      resetPlannedTwinIDUpdate(plannedTwinIDUpdateDispatch);
    } catch (err) {
      const { status, body } = err as RequestError;
      modal.error({
        title: status,
        content: body.msg,
      });
      return;
    } finally {
      resetPlannedTwinIDUpdate(plannedTwinIDUpdateDispatch);

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }

    applyUploadCompleteModal();
  };

  const reportFields = reportFieldsKeyedByPlanID
    ?.[selectedPlan?.assetTypeId!]
    ?.[selectedPlan?.planID!] ?? [];
  const exampleCsv = (
    <Table
      size="small"
      pagination={false}
      dataSource={[
        {
          key: '1',
          ...reportFields?.reduce((acc, field) => {
            const getValue = () => {
              switch (field.label) {
                case 'twinID':
                  return 'MLR-1234';
                case 'sequence':
                  return '1';
                default:
                  return displayDateInProjectTime();
              }
            };
            return ({ ...acc, [field.label]: getValue() });
          }, {}),
        },
      ]}
      columns={reportFields?.map(field => ({
        title: field.label,
        dataIndex: field.label,
        key: field.value,
        ellipsis: false,
      }))}
    />
  );

  const handleEditPlanLabelSubmit = async (values: any) => {
    const { label } = values;
    setIsEditPlanLabelSubmitLoading(true);
    const res = await fetchBlueprint(tenant, projectCode);
    const { asset_types: assetTypes } = res;
    if (!selectedPlan || !assetTypes) return;

    assetTypes.types.find(type => type.id === selectedPlan?.assetTypeId)!.plans!
      .find(plan => plan.id === selectedPlan?.planID)!.label = label;
    await update(
      tenant,
      projectId,
      projectCode,
      { asset_types: assetTypes },
    );

    setIsEditPlanLabelModalOpen(false);
    setIsEditPlanLabelSubmitLoading(false);
  };
  return (
    <Content className={styles.container}>
      <div className={styles.leftContainer}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Plan Update Tool
        </Typography.Title>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Step 1: Select Asset Type & Plan
        </Typography.Title>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
          <SegmentedPicklist
            columns={planPicklistColumns}
            onValueChange={(value) => {
              setSelectedPlan(value as any);
            }}
            value={selectedPlan}
            resetValuesOnChange
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setIsEditPlanLabelModalOpen(true)}
            disabled={!selectedPlan}
          />
        </div>
        <Modal
          title="Edit Plan"
          open={isEditPlanLabelModalOpen}
          closable={false}
          footer={[]}
          destroyOnClose
          style={{ padding: 0 }}
        >
          <ModalForm
            fields={[
              {
                entryComponent: 'text',
                id: 'label',
                label: 'Label',
                isRequired: true,
              },
            ]}
            onSubmit={values => handleEditPlanLabelSubmit(values)}
            onCancel={() => setIsEditPlanLabelModalOpen(false)}
            submitButtonLoading={isEditPlanLabelSubmitLoading}
            defaultValues={{
              label: planConfigurationsKeyedById.get(selectedPlan?.planID!)?.label,
            }}
          />
        </Modal>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Step 2: Select Update Mode
        </Typography.Title>
        <Radio.Group
          value={plannedTwinIDUpdateMode}
          onChange={(e) => { handleChangeCsvOption(e.target.value); }}
          style={{ marginBottom: 16 }}
          buttonStyle="solid"
        >
          <Radio.Button value="upsert">
            Planned Twin IDs to
            {' '}
            <b>Update</b>
            {' '}
            and
            {' '}
            <b>Add</b>
            {' '}
            (No Delete)
          </Radio.Button>
          <Radio.Button value="complete">
            Planned Twin IDs to
            {' '}
            <b>Update</b>
            ,
            {' '}
            <b>Add</b>
            {' '}
            and
            <b style={{ color: 'red' }}> Delete</b>
          </Radio.Button>
          {/* Append is deprecated, it's too similar to upsert
                      <Radio.Button value={'append'}>
                        I have a List of ONLY New Asset to Add
                      </Radio.Button> */}
        </Radio.Group>
      </div>
      <UpdateToolUploadPanel
        isPlanning={isPlanningPlannedTwinIDs}
        isApplying={isApplyingPlannedTwinIDs}
        planComplete={!!plannedUpdatedPlannedTwinID}
        exampleCsv={exampleCsv}
        onApply={handleApplyPlannedTwinIDs}
        onExport={async () => {
          await exportToSpreadsheet(
            jsonExportData,
            `export_${tenant}_${project}_${dayjs().format('YYYY-MM-DD')}`,
          );
        }}
        isApplyDisabled={!applyEnabled()}
        onFileUpload={onFileUpload}
        fileName={fileName}
      />
      <UpdateToolLogStats
        errorCount={errorCount}
        warningCount={warningCount}
        infoCount={infoCount}
        isLoading={isPlanningPlannedTwinIDs}
      />

      <PlannedTwinIDUpdateToolResults />
    </Content>
  );
};

export default PlannedTwinIDUpdateTool;
